/**
 * MarketplaceSearch.jsx
 * 
 * Search results page for the marketplace.
 * Features:
 * - Category pills at top for quick filtering
 * - Clean search header with logo
 * - Experience cards with custom shadows
 * - Filters for city, category, sorting
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { SocialLinksLight } from '../components/SocialLinks';
import useSWR from 'swr';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Search, 
  MapPin, 
  Star, 
  Clock, 
  Filter,
  X,
  Globe,
  SlidersHorizontal,
  Loader2,
  ChevronRight,
  Compass,
  User
} from 'lucide-react';
import { QUICK_FILTER_CATEGORIES, getCategoryName } from '../constants/categories';
import { getCityImage } from '../constants/cityImages';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import CustomerSignInModal from '../components/CustomerSignInModal';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// SWR fetcher
const fetcher = async (url) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to fetch');
  return res.json();
};

// Debounce hook for search input
const useDebounce = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  
  return debouncedValue;
};

// Use centralized categories
const quickCategories = QUICK_FILTER_CATEGORIES;

// Skeleton loader for experience cards
const ExperienceCardSkeleton = () => (
  <Card 
    className="overflow-hidden rounded-2xl border-0 bg-white"
    style={{ boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)' }}
  >
    <div className="aspect-[4/3] bg-gray-200 animate-pulse" />
    <CardContent className="p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="h-3 bg-gray-200 rounded w-16 animate-pulse" />
        <div className="h-3 bg-gray-200 rounded w-12 animate-pulse" />
      </div>
      <div className="h-5 bg-gray-200 rounded w-full mb-2 animate-pulse" />
      <div className="h-4 bg-gray-200 rounded w-3/4 mb-3 animate-pulse" />
      <div className="flex items-center gap-2 mb-3">
        <div className="h-4 bg-gray-200 rounded w-10 animate-pulse" />
        <div className="h-4 bg-gray-200 rounded w-24 animate-pulse" />
      </div>
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="h-4 bg-gray-200 rounded w-16 animate-pulse" />
        <div className="h-5 bg-gray-200 rounded w-14 animate-pulse" />
      </div>
    </CardContent>
  </Card>
);

const MarketplaceSearch = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { customer, isAuthenticated } = useCustomerAuth();
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchRef = React.useRef(null);
  
  // URL params
  const cityParam = searchParams.get('city') || '';
  const categoryParam = searchParams.get('category') || '';
  const queryParam = searchParams.get('q') || '';
  
  // State - only for controlled inputs
  const [searchQuery, setSearchQuery] = useState(queryParam);
  const [selectedCity, setSelectedCity] = useState(cityParam);
  const [selectedCategory, setSelectedCategory] = useState(categoryParam);
  const [activeQuickFilter, setActiveQuickFilter] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [offset, setOffset] = useState(0);
  const limit = 20;

  // Debounce search query for API calls (300ms delay)
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // SWR for filter options (cities & categories) - cached aggressively
  const { data: citiesData } = useSWR(
    `${API_URL}/api/marketplace/cities`,
    fetcher,
    { revalidateOnFocus: false, dedupingInterval: 120000, errorRetryCount: 2 }
  );

  const { data: categoriesData } = useSWR(
    `${API_URL}/api/marketplace/categories`,
    fetcher,
    { revalidateOnFocus: false, dedupingInterval: 120000, errorRetryCount: 2 }
  );

  // Build search URL for SWR - uses debounced query
  const searchUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (selectedCity) params.append('city', selectedCity.slice(0, 100));
    if (selectedCategory) params.append('category', selectedCategory.slice(0, 50));
    if (debouncedSearchQuery) params.append('q', debouncedSearchQuery.slice(0, 200));
    params.append('sortBy', sortBy);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    return `${API_URL}/api/marketplace/search?${params.toString()}`;
  }, [selectedCity, selectedCategory, debouncedSearchQuery, sortBy, offset]);

  // SWR for search results - revalidates when URL changes
  const { data: searchData, isLoading: loading } = useSWR(
    searchUrl,
    fetcher,
    { 
      revalidateOnFocus: false, 
      dedupingInterval: 10000,
      keepPreviousData: true, // Keep showing old data while loading new
      errorRetryCount: 2 
    }
  );

  // Derived state from SWR
  const cities = citiesData?.cities || [];
  const categories = categoriesData?.categories || [];
  const results = searchData?.services || [];
  const totalCount = searchData?.total || 0;

  // Set active quick filter based on category param
  useEffect(() => {
    if (categoryParam) {
      const match = quickCategories.find(c => c.id === categoryParam);
      if (match) {
        setActiveQuickFilter(categoryParam);
      }
    }
  }, [categoryParam]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setOffset(0);
    
    // Update URL params
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (selectedCity) params.set('city', selectedCity);
    if (selectedCategory) params.set('category', selectedCategory);
    setSearchParams(params);
  };

  const handleCityChange = (value) => {
    setSelectedCity(value === 'all' ? '' : value);
    setOffset(0);
  };

  const handleQuickFilter = (categoryId) => {
    setActiveQuickFilter(categoryId);
    if (categoryId === 'all') {
      setSelectedCategory('');
    } else {
      setSelectedCategory(categoryId);
    }
    setOffset(0);
  };

  const clearFilters = () => {
    setSelectedCity('');
    setSelectedCategory('');
    setSearchQuery('');
    setActiveQuickFilter('all');
    setOffset(0);
    setSearchParams({});
  };

  const handleExperienceClick = (service) => {
    navigate(`/experience/${service.id}${searchParams.get('ref') ? `?ref=${searchParams.get('ref')}` : ''}`);
  };

  const formatPrice = (price, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours} hour${hours > 1 ? 's' : ''}`;
  };

  const hasActiveFilters = selectedCity || selectedCategory || searchQuery;

  const getSearchTitle = () => {
    if (selectedCity && selectedCategory && selectedCategory !== 'all') {
      const cat = quickCategories.find(c => c.id === selectedCategory);
      return `${cat?.name || selectedCategory} in ${selectedCity}`;
    }
    if (selectedCity) return `${selectedCity} Experiences`;
    if (selectedCategory && selectedCategory !== 'all') {
      const cat = quickCategories.find(c => c.id === selectedCategory);
      return cat?.name || selectedCategory;
    }
    if (searchQuery) return `Results for "${searchQuery}"`;
    return 'All Experiences';
  };

  // Filter cities based on search input for dropdown
  const filteredCities = cities.filter(city => 
    city.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (city.country && city.country.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 pt-[env(safe-area-inset-top)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-4">
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <img 
                src="/logo-icon.png" 
                alt="BookingFlows" 
                className="h-9 w-9 object-contain rounded-xl"
              />
              <span className="text-xl font-bold text-gray-900 hidden sm:block">BookingFlows</span>
            </Link>

            {/* Search Bar with Dropdown */}
            <div ref={searchRef} className="flex-1 max-w-xl hidden md:block relative">
              <form onSubmit={handleSearch}>
                <div className="flex items-center gap-2 p-1.5 bg-white rounded-full border border-gray-200">
                  <div className="flex-1 flex items-center gap-2 pl-3">
                    <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value.slice(0, 100));
                        setShowSearchDropdown(true);
                      }}
                      onFocus={() => setShowSearchDropdown(true)}
                      placeholder="Where to?"
                      maxLength={100}
                      className="flex-1 border-0 outline-none text-sm placeholder:text-gray-400 bg-transparent"
                      data-testid="search-input"
                    />
                  </div>
                  <div className="hidden sm:flex items-center gap-1 px-2 text-xs text-gray-400 border-l border-gray-200">
                    <Globe className="w-3 h-3" />
                    <span>USA</span>
                  </div>
                  <Button 
                    type="submit"
                    size="sm"
                    className="rounded-full px-4 text-xs bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                  >
                    Search
                  </Button>
                </div>
              </form>

              {/* City Dropdown */}
              {showSearchDropdown && (filteredCities.length > 0 || cities.length > 0) && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-200 z-[100]">
                  <div className="p-3 border-b border-gray-100">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {searchQuery ? 'Matching Cities' : 'Popular Destinations'}
                    </p>
                  </div>
                  <div className="max-h-[280px] overflow-y-auto">
                    {(searchQuery ? filteredCities : cities).slice(0, 5).map((city) => (
                      <button
                        key={city.city}
                        onClick={() => {
                          setShowSearchDropdown(false);
                          setSelectedCity(city.city);
                          setOffset(0);
                        }}
                        className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors text-left"
                      >
                        <div className="w-12 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                          <img
                            src={getCityImage(city.city)}
                            alt={city.city}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">{city.city}</p>
                          <p className="text-xs text-gray-500">{city.country}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xs text-gray-400">{city.operatorCount} {city.operatorCount === 1 ? 'provider' : 'providers'}</p>
                        </div>
                      </button>
                    ))}
                    {filteredCities.length === 0 && searchQuery && (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        <p>No destinations found for "{searchQuery}"</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 flex-shrink-0">
              <Link to="/for-suppliers" className="text-sm text-gray-600 hover:text-gray-900 hidden md:block">
                List Your Business
              </Link>
              {isAuthenticated ? (
                <Link to="/my-trips">
                  <div className="flex items-center gap-2 px-2 py-1 rounded-full border border-gray-200 hover:shadow-md transition-shadow cursor-pointer">
                    {customer?.avatar_url ? (
                      <img 
                        src={customer.avatar_url} 
                        alt={customer.name}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="w-4 h-4 text-gray-500" />
                      </div>
                    )}
                    <span className="hidden sm:inline text-sm font-medium text-gray-700 pr-1">
                      {customer?.name?.split(' ')[0] || 'My Trips'}
                    </span>
                  </div>
                </Link>
              ) : (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="rounded-full"
                  onClick={() => setShowSignInModal(true)}
                >
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Category Pills */}
      <div className="border-b border-gray-100 bg-white sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 py-3 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
            {/* Selected category at the front with X */}
            {activeQuickFilter && activeQuickFilter !== 'all' && (
              <button
                onClick={() => handleQuickFilter('all')}
                className="flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap flex-shrink-0 bg-gray-900 text-white shadow-lg transition-all duration-200"
                data-testid="selected-category"
              >
                <span className="text-base">{quickCategories.find(c => c.id === activeQuickFilter)?.icon}</span>
                <span>{quickCategories.find(c => c.id === activeQuickFilter)?.name}</span>
                <X className="w-4 h-4 ml-1 hover:text-gray-300" />
              </button>
            )}
            {/* All other categories */}
            {quickCategories
              .filter(cat => cat.id !== activeQuickFilter)
              .map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleQuickFilter(cat.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap flex-shrink-0 transition-all duration-200 ${
                    cat.id === 'all' && activeQuickFilter === 'all'
                      ? 'bg-gray-900 text-white shadow-lg'
                      : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                  data-testid={`quick-filter-${cat.id}`}
                >
                  <span className="text-base">{cat.icon}</span>
                  <span>{cat.name}</span>
                </button>
              ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Results Header with Filters */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{getSearchTitle()}</h1>
            <p className="text-gray-500 mt-1">
              {loading ? 'Searching...' : `${totalCount} ${totalCount === 1 ? 'experience' : 'experiences'} available`}
            </p>
          </div>

          {/* Filter Dropdowns */}
          <div className="flex flex-wrap items-center gap-3">
            <Select value={selectedCity || 'all'} onValueChange={handleCityChange}>
              <SelectTrigger className="w-[160px] h-10 rounded-full bg-white border-gray-200 shadow-sm hover:shadow transition-shadow" data-testid="city-filter">
                <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                <SelectValue placeholder="Any city" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any city</SelectItem>
                {cities.map((city) => (
                  <SelectItem key={city.city} value={city.city}>
                    {city.city}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[170px] h-10 rounded-full bg-white border-gray-200 shadow-sm hover:shadow transition-shadow" data-testid="sort-filter">
                <SlidersHorizontal className="w-4 h-4 mr-2 text-gray-400" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="popular">Most Popular</SelectItem>
                <SelectItem value="price_low">Price: Low to High</SelectItem>
                <SelectItem value="price_high">Price: High to Low</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
                className="text-gray-600 hover:text-gray-900 rounded-full border-gray-200 hover:border-gray-300"
                data-testid="clear-filters"
              >
                <X className="w-4 h-4 mr-1" />
                Clear all
              </Button>
            )}
          </div>
        </div>

        {/* Results Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <ExperienceCardSkeleton key={i} />
            ))}
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Compass className="w-10 h-10 text-gray-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No experiences found</h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              We couldn&apos;t find any experiences matching your criteria. Try adjusting your filters or exploring a different category.
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button onClick={clearFilters} variant="outline" className="rounded-full">
                Clear all filters
              </Button>
              <Link to="/">
                <Button className="rounded-full bg-gray-900 hover:bg-gray-800">
                  Explore destinations
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {results.map((service) => (
                <Card 
                  key={service.id}
                  className="group cursor-pointer overflow-hidden rounded-2xl border-0 bg-white transition-all duration-300"
                  style={{ 
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
                  }}
                  onClick={() => handleExperienceClick(service)}
                  data-testid={`result-card-${service.id}`}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 25px 50px -12px rgb(0 0 0 / 0.25)';
                    e.currentTarget.style.transform = 'translateY(-4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                    {service.mainImage || (service.images && service.images[0]) ? (
                      <>
                        <img
                          src={service.mainImage || service.images[0]}
                          alt={service.name}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            e.target.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                        <div className="hidden w-full h-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center absolute inset-0">
                          <Globe className="w-12 h-12 text-blue-300" />
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                        <Globe className="w-12 h-12 text-blue-300" />
                      </div>
                    )}
                    {service.operator?.featured && (
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 bg-amber-400 text-amber-900 text-xs font-semibold rounded-full">
                          Featured
                        </span>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" />
                        <span>{service.city || 'Location TBD'}</span>
                        {service.category && (
                          <>
                            <span className="mx-1">·</span>
                            <span>{service.category}</span>
                          </>
                        )}
                      </div>
                      {service.operator?.rating > 0 && (
                        <span className="flex items-center gap-0.5">
                          {[1, 2, 3, 4, 5].map((star) => {
                            const rating = service.operator.rating || 0;
                            const roundedRating = Math.ceil(rating * 2) / 2;
                            const filled = star <= Math.floor(roundedRating);
                            return (
                              <Star 
                                key={star}
                                className={`w-3 h-3 ${filled ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
                              />
                            );
                          })}
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 line-clamp-2 mb-2 group-hover:text-blue-600 transition-colors">
                      {service.name}
                    </h3>
                    {service.operator && (
                      <p className="text-sm text-gray-500 mb-3">
                        by {service.operator.name}
                      </p>
                    )}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <span className="flex items-center gap-1 text-sm text-gray-500">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDuration(service.durationMinutes)}
                      </span>
                      <div className="text-right">
                        <span className="font-bold text-lg" style={{ color: '#2563eb' }}>
                          {formatPrice(service.price, service.currency)}
                        </span>
                        {service.priceType === 'per_person' && (
                          <span className="text-xs text-gray-500">/person</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {totalCount > limit && (
              <div className="flex items-center justify-center gap-4 mt-12">
                <Button
                  variant="outline"
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                  className="rounded-full"
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Page {Math.floor(offset / limit) + 1} of {Math.ceil(totalCount / limit)}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setOffset(offset + limit)}
                  disabled={offset + limit >= totalCount}
                  className="rounded-full"
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <footer className="py-12 px-4 bg-white border-t border-gray-100 mt-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img 
                src="/logo-icon.png" 
                alt="BookingFlows" 
                className="h-8 w-8 object-contain rounded-xl"
              />
              <span className="text-lg font-bold text-gray-900">BookingFlows</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600">
              <Link to="/" className="hover:text-blue-600 transition-colors">Home</Link>
              <Link to="/search" className="hover:text-blue-600 transition-colors">All Experiences</Link>
              <Link to="/for-suppliers" className="hover:text-blue-600 transition-colors">For Operators</Link>
              <Link to="/terms" className="hover:text-blue-600 transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-blue-600 transition-colors">Privacy</Link>
            </div>
            <div className="flex flex-col items-center gap-3">
              <SocialLinksLight />
              <p className="text-sm text-gray-500">
                © {new Date().getFullYear()} BookingFlows
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Customer Sign In Modal */}
      <CustomerSignInModal 
        open={showSignInModal} 
        onOpenChange={setShowSignInModal} 
      />

      {/* Bottom fade effect for smooth scrolling */}
      <div 
        className="fixed bottom-0 left-0 right-0 h-16 pointer-events-none z-40"
        style={{
          background: 'linear-gradient(to top, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 100%)'
        }}
      />
    </div>
  );
};

export default MarketplaceSearch;
